import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { DrillDataVisualizationComponent } from 'app/modules/reporting/@components/builder/visualizations/drill-data/drill-data.component';
import { formatFilterItem } from 'app/modules/reporting/reporting.functions';
import { DrillFilter, ReportLayoutChart, ReportLayoutDefinition } from 'app/modules/reporting/reporting.types';
import { ReportLayout } from 'app/modules/reports/reports.types';
import { getAllowedFields } from 'app/shared/functions';
import { GraphqlFilter } from 'app/shared/models/graphql';
import { filter, noop, Observable, tap } from 'rxjs';
import { DashboardService } from '../dashboard.service';
import { Dashboard } from '../dashboard.types';
import { GetDashboardById, UpdateDashboard } from '../state/actions';
import { DashboardState } from '../state/state';
import { DashboardReportSelectComponent } from './report-select/report-select.component';
import { AuthService } from 'app/core';
import { FeatureFlag, NewPermission, Permission } from 'app/core/enums';
import { AccountState } from '@state/account/state';
import { RelatedToType } from '@shared/enums';

type DataPointChart = ReportLayoutChart & {
  reportLayout: ReportLayout<ReportLayoutDefinition>;
  drillFilters: DrillFilter;
};
type DashboardDefinition = { report_ids: number[][] };

@Component({
  selector: 'app-dashboard-details-report',
  templateUrl: './details-report.component.html',
  styleUrls: ['./details-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardDetailsReportComponent extends OnDestroyMixin implements OnInit {
  @Input() set dashboard(value: Dashboard) {
    this._dashboard = value;
    this.store.dispatch(new GetDashboardById(value.id));
  }

  @Input() fields: FieldDef[];
  @Input() options: any;

  @Select(DashboardState.getDashboardById) selectedDashboardState$: Observable<Dashboard>;

  ui = { editMode: false, editingName: false };
  selectedDashboard$: Observable<Dashboard>;
  hasNestedFiltersOn = false;
  protected readonly Permission = Permission;
  protected readonly NewPermission = NewPermission;
  protected readonly newPermissionsEnabled$ = this.store.select(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  private _dashboard: Dashboard;

  get dashboard(): Dashboard {
    return this._dashboard;
  }

  get dashboardName(): string {
    return this._dashboard?.name ?? 'Untitled';
  }

  set dashboardName(value: string) {
    if (this.dashboard) {
      this._dashboard.name = value;
    }
  }

  constructor(
    private readonly matDialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dashboardService: DashboardService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
  ) {
    super();

    this.hasNestedFiltersOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.NESTED_FILTERS);
  }

  ngOnInit(): void {
    this.selectedDashboard$ = this.selectedDashboardState$.pipe(
      tap((dashboard) => {
        this._dashboard = dashboard;
      }),
    );

    const queryParams = this.activatedRoute.snapshot.queryParams;
    if (queryParams.editmode === 'true') {
      this.toggleEditMode();
    }
  }

  cancel(): void {
    this.ui = { ...this.ui, editMode: false };
    this.store.dispatch(new GetDashboardById(this.dashboard.id));
  }

  save(): void {
    this.ui = { ...this.ui, editMode: false };
    this._dashboard.definition = JSON.stringify({
      report_ids: (JSON.parse(this._dashboard.definition) as DashboardDefinition).report_ids.filter(
        (row) => row.length,
      ),
    });
    this.store.dispatch(new UpdateDashboard(this._dashboard));
  }

  delete(): void {
    this.dashboardService
      .deleteDashboard(this.dashboard.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.router.navigate(['../'], { relativeTo: this.activatedRoute }));
  }

  manageChart(
    mode: 'first' | 'addInColumn' | 'addInNewRow' | 'remove',
    data?: { rowIndex: number; colIndex?: number },
  ): void {
    const definition: DashboardDefinition = JSON.parse(this._dashboard.definition);

    if (mode === 'remove') {
      definition.report_ids[data.rowIndex] = definition.report_ids[data.rowIndex].filter(
        (_, index) => index !== data.colIndex,
      );
      this._dashboard.charts[data.rowIndex] = this._dashboard.charts[data.rowIndex].filter(
        (_, index) => index !== data.colIndex,
      );
      this._dashboard = { ...this._dashboard, definition: JSON.stringify(definition) };
    } else {
      this.matDialog
        .open(DashboardReportSelectComponent, { autoFocus: false })
        .afterClosed()
        .pipe(
          untilComponentDestroyed(this),
          filter((result) => result),
          tap((reportLayout: ReportLayout<ReportLayoutDefinition>) => {
            switch (mode) {
              case 'first':
                this._dashboard.definition = JSON.stringify({ report_ids: [[reportLayout.id]] });
                this._dashboard.charts = [[{ title: reportLayout.name }]];
                this.save();
                break;
              case 'addInColumn':
                definition.report_ids[data.rowIndex].push(reportLayout.id);
                this._dashboard.charts[data.rowIndex].push({ title: reportLayout.name });
                break;
              case 'addInNewRow':
                definition.report_ids.push([reportLayout.id]);
                this._dashboard.charts.push([{ title: reportLayout.name }]);
                break;
            }

            this._dashboard = { ...this._dashboard, definition: JSON.stringify(definition) };

            this.changeDetectorRef.detectChanges();
          }),
        )
        .subscribe(noop);
    }
  }

  toggleEditingName(): void {
    this.ui = { ...this.ui, editingName: !this.ui.editingName };
  }

  toggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
  }

  onDataPointSelection(event: { seriesIndex: number; dataPointIndex: number }, chart: DataPointChart): void {
    const innerFilters = [chart.drillFilters[event.dataPointIndex]];
    const layoutFilter = JSON.parse(
      (JSON.parse(chart.reportLayout.filters) as unknown as GraphqlFilter).fjson,
    ) as any[];
    const category = chart.rankedCategories[event.dataPointIndex];
    const seriesValue = chart.series_value;
    let series;
    if (seriesValue) {
      const fieldType = this.fields.find(({ name }) => name === seriesValue)?.type;
      const isRecord = fieldType === 'record';
      series = chart.series[event.seriesIndex][seriesValue.endsWith('id') || isRecord ? 'value' : 'name'];
      innerFilters.push(formatFilterItem(seriesValue, series, chart.series[event.seriesIndex], this.fields));
    }
    const data = {
      relatedToType: chart.reportLayout.definition.related_to_type,
      category,
      series,
      columns: chart.reportLayout.definition.columns,
      filters: {
        fjson: JSON.stringify([...layoutFilter, ...innerFilters]),
      },
      fields: getAllowedFields(this.fields, chart.reportLayout.definition.related_to_type, this.hasNestedFiltersOn),
      options: this.options,
    };

    this.matDialog
      .open(DrillDataVisualizationComponent, {
        autoFocus: false,
        width: '100%',
        data,
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  getFieldDefs(chart) {
    if (chart.reportLayout) {
      if (chart.reportLayout.definition.related_to_type === RelatedToType.Trail) {
        return this.fields
          .filter((field) => [RelatedToType.Project, RelatedToType.Trail].includes(field.related_to_type))
          .map((field) => {
            if (field.related_to_type === RelatedToType.Project) {
              return { ...field, name: `related_to.${field.name}` };
            }

            return { ...field };
          });
      }

      return getAllowedFields(this.fields, chart.reportLayout.definition.related_to_type);
    } else {
      return [];
    }
  }

  canShowThreeDotMenu(): boolean {
    if (this.ui.editMode) {
      return this.authService.hasPermission(Permission.DASHBOARDS_DELETE);
    } else {
      return this.authService.hasAnyPermission(Permission.DASHBOARDS_CREATE_EDIT, Permission.DASHBOARDS_DELETE);
    }
  }

  getEditDashboardPermission() {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return NewPermission.DASHBOARDS_EDIT;
    } else {
      return Permission.DASHBOARDS_CREATE_EDIT;
    }
  }
}
